/* WEEK EDITOR STAGE 2 IN THE EXAMPLE (2026-10-19, `docs/WEEK-EDITOR-STAGE2-PLAN.md`). William judges the web in the example,
 * so the stage-2 writes must WORK there, applied to THIS TAB's made-up world in memory (gone on reload), never sent:
 *   • `assign_coach_workout` — a library workout dropped on a day (the panel's Workouts tab; the Assign window too);
 *   • the day's order of workouts — `PATCH coach_assignments` carrying `day_position` alone (where the dropped one lands; the
 *     4-arrow drag too);
 *   • `save_week_as_program` — a week saved as a program.
 * Each mirrors the SQL's rules a coach can SEE (a template is not a client copy; one template once per day); it is not the
 * permission proof — that is the rehearsal against the live database. The clone copies EVERY column of every row (spread),
 * as `clone_coach_workout` accounts for every column. */
import type { Fixture } from "./rest";
import type { ProgramDay } from "../weekProgram";
import { ONE_SOURCE_PER_DAY_INDEX } from "../assign";

type Row = Record<string, unknown>;
const newId = (): string => crypto.randomUUID();

/** `clone_coach_workout`, in memory: a copy of the whole tree, new ids, every column carried. */
function cloneWorkout(fx: Fixture, sourceId: string, over: { client_id: string | null; root_workout_id: string | null; name?: string }, stamp: string): string | null {
  const t = fx.tables;
  const src = (t["coach_workouts"] ?? []).find((w) => w["id"] === sourceId);
  if (src === undefined) return null;
  const id = newId();
  (t["coach_workouts"] ??= []).push({ ...src, id, ...over, name: over.name ?? src["name"], archived_at: null, created_at: stamp, updated_at: stamp });
  for (const s of (t["coach_workout_sections"] ?? []).filter((x) => x["workout_id"] === sourceId)) {
    const sid = newId();
    t["coach_workout_sections"]!.push({ ...s, id: sid, workout_id: id });
    for (const e of (t["coach_workout_exercises"] ?? []).filter((x) => x["section_id"] === s["id"])) {
      const eid = newId();
      t["coach_workout_exercises"]!.push({ ...e, id: eid, section_id: sid });
      for (const x of (t["coach_workout_sets"] ?? []).filter((r) => r["workout_exercise_id"] === e["id"])) {
        t["coach_workout_sets"]!.push({ ...x, id: newId(), workout_exercise_id: eid });
      }
    }
  }
  return id;
}

/** `assign_coach_workout(p_workout_id, p_client_id, p_scheduled_date, p_note)` — returns the new assignment's id, or the
 *  one-per-day refusal in the index's own name (what `describeOutcome` reads). */
export function applyAssign(fx: Fixture, args: Row, stamp: string): { data: string } | { error: string } {
  const t = fx.tables;
  const tpl = (t["coach_workouts"] ?? []).find((w) => w["id"] === args["p_workout_id"]);
  if (tpl === undefined) return { error: `clone_coach_workout: workout ${String(args["p_workout_id"])} not found or not readable` };
  const root = (tpl["root_workout_id"] as string | null) ?? (tpl["id"] as string);
  const day = String(args["p_scheduled_date"]);
  const client = String(args["p_client_id"]);
  const assignments = (t["coach_assignments"] ??= []);
  if (assignments.some((a) => a["client_id"] === client && a["scheduled_date"] === day && ((a["root_workout_id"] as string | null) ?? a["workout_id"]) === root)) {
    return { error: `duplicate key value violates unique constraint "${ONE_SOURCE_PER_DAY_INDEX}"` };
  }
  const copy = cloneWorkout(fx, String(args["p_workout_id"]), { client_id: client, root_workout_id: root }, stamp)!;
  const id = newId();
  const onDay = assignments.filter((a) => a["client_id"] === client && a["scheduled_date"] === day).length;
  assignments.push({
    id, coach_id: fx.coachId, client_id: client, workout_id: copy, root_workout_id: root, scheduled_date: day, status: "assigned",
    hidden_from_client: false, is_deload: false, note: String(args["p_note"] ?? ""), assigned_at: stamp, completed_at: null,
    completed_minutes: [], completed_snapshot: null, day_position: onDay,
  });
  return { data: id };
}

/** `PATCH coach_assignments?id=eq.<id>` with `{ day_position }` alone — the day's order, in memory. */
export function isDayOrderWrite(method: string, path: string, body: unknown): boolean {
  if (method.toUpperCase() !== "PATCH" || path !== "/rest/v1/coach_assignments" || typeof body !== "string") return false;
  try {
    const parsed: unknown = JSON.parse(body);
    return parsed !== null && typeof parsed === "object" && Object.keys(parsed).join(",") === "day_position"
      && Number.isInteger((parsed as Row)["day_position"]);
  } catch { return false; }
}

export function applyDayOrder(fx: Fixture, url: URL, body: string): void {
  const id = /^eq\.(.+)$/.exec(url.searchParams.get("id") ?? "")?.[1];
  const a = (fx.tables["coach_assignments"] ?? []).find((x) => x["id"] === id);
  if (a !== undefined) a["day_position"] = (JSON.parse(body) as Row)["day_position"];
}

/** `save_week_as_program(p_name, p_days)` — each copy cloned into a template (no client, no root, its given name), the
 *  program head, its day rows (week 1). Refuses a template source and two clients, as the SQL does. */
export function applySaveWeekAsProgram(fx: Fixture, args: Row, stamp: string): { data: string } | { error: string } {
  const t = fx.tables;
  const name = String(args["p_name"] ?? "").trim();
  const days = (args["p_days"] ?? []) as ProgramDay[];
  if (name === "") return { error: "PROGRAM-NAME: a name" };
  if (days.length === 0) return { error: "PROGRAM-EMPTY: no days" };
  const clients = new Set<unknown>();
  for (const d of days) {
    const w = (t["coach_workouts"] ?? []).find((x) => x["id"] === d.workout_id);
    if (w === undefined || w["client_id"] === null || w["client_id"] === undefined) return { error: "PROGRAM-TEMPLATE: not a client copy" };
    clients.add(w["client_id"]);
    if (d.weekday < 1 || d.weekday > 7) return { error: "PROGRAM-DAYS: weekday" };
  }
  if (clients.size > 1) return { error: "PROGRAM-CLIENTS: two clients" };
  const programId = newId();
  (t["coach_programs"] ??= []).push({ id: programId, coach_id: fx.coachId, name, description: null, created_at: stamp, updated_at: stamp, archived_at: null });
  for (const d of days) {
    const tpl = cloneWorkout(fx, d.workout_id, { client_id: null, root_workout_id: null, name: d.template_name }, stamp)!;
    (t["coach_program_days"] ??= []).push({ id: newId(), program_id: programId, week_index: 1, weekday: d.weekday, position: d.position, workout_id: tpl, note: null });
  }
  return { data: programId };
}
